import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { debounce } from '@/utils';
import { DateRange } from '@/components/common/DateRangePicker';
import {
  // useGetCompetitionByIdQuery, 
  // useGetEventsQuery, 
  useGetPendingMarketsQuery
} from '@/store/api/eventApi';
import { MarketRunners, useLazyGetMarketRunnerQuery } from '@/store/api/runnersApi';
import {
  DeclareResultPayload, useDeclareResultMutation, useRollbackResultMutation,
} from '@/store/api/betResultApi';
import { PendingMarketItem } from '@/types/pendingMarket';
import { useGetRollbackMarketsQuery } from '@/store/api/eventApi';
import dayjs from 'dayjs';

export type ResultValue = {
  status: 'Winner' | 'Looser' | 'None';
  selectionId: string;
};

const sportOptions = [
  { label: 'All', value: 'all' },
  { label: 'Cricket', value: 'Cricket' },
  { label: 'Football', value: 'Soccer' },
  { label: 'Tennis', value: 'Tennis' },
];
export type ResultTabType = 'pending' | 'rollback';
type ResultActionType = 'declare' | 'rollback';

export const useEventListing = () => {
  const [isPollingEnabled, setIsPollingEnabled] = useState(false);
  const [sport, setSport] = useState<string>('all');
  // const [competitionId, setCompetitionId] = useState<number | undefined>(undefined);
  // const [eventId, setEventId] = useState<string | undefined>('all');
  const [activeResultTab, setActiveResultTab] = useState<ResultTabType>('pending');

  // pagination & filters
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [dateRange, setDateRange] = useState<DateRange>({
    from: dayjs().startOf("day").toDate(),
    to: dayjs().endOf("day").toDate(),
  });

  // modal state
  const [open, setOpen] = useState(false);
  const [selectedEventName, setSelectedEventName] = useState('');
  const [runners, setRunners] = useState<MarketRunners>({} as MarketRunners);
  const [eventDetails, setEventDetails] = useState<PendingMarketItem>();

  // APIs
  const { data, isLoading, isFetching } = useGetPendingMarketsQuery({
    page,
    limit,
    search,
    ...(dateRange?.from && {
      fromDate: dayjs(dateRange.from).toISOString(),
    }),
    ...(dateRange?.to && {
      toDate: dayjs(dateRange.to).toISOString(),
    }),
    // eventId: eventId !== 'all' ? Number(eventId) : undefined,
    sport: sport !== 'all' ? sport : undefined,
  },
    {
      skip: activeResultTab !== 'pending',
      pollingInterval: isPollingEnabled && activeResultTab === 'pending' ? 30000 : 0,
    }
  );

  const {
    data: rollbackData,
    isLoading: rollbackLoading,
    isFetching: rollbackFetching,
  } = useGetRollbackMarketsQuery(
    {
      page,
      limit,
      search,
      ...(dateRange?.from && {
        fromDate: dayjs(dateRange.from).toISOString(),
      }),
      ...(dateRange?.to && {
        toDate: dayjs(dateRange.to).toISOString(),
      }),
      // eventId: eventId !== 'all' ? Number(eventId) : undefined,
      sport: sport !== 'all' ? sport : undefined,
    },
    {
      skip: activeResultTab !== 'rollback',
      pollingInterval: isPollingEnabled && activeResultTab === 'rollback' ? 30000 : 0,
    }
  );

  const [fetchRunner, { isLoading: runnersLoading }] = useLazyGetMarketRunnerQuery();
  const [declareResult, { isLoading: betResultLoading }] = useDeclareResultMutation();
  const [rollbackResult, { isLoading: rollbackResultLoading }] =
    useRollbackResultMutation();
  const isActionLoading = betResultLoading || rollbackResultLoading;

  //events api
  // const { data: competitionData, isFetching: competitionLoading } = useGetCompetitionByIdQuery(sport, {
  //   skip: sport === 'all',
  // });
  // const competitionOptions = [
  //   ...(competitionData?.competitions?.map((c) => ({
  //     label: c.name,
  //     value: c.id,
  //   })) || []),
  // ];

  // const { data: eventsData, isFetching: eventsLoading } = useGetEventsQuery(
  //   { competitionId },
  //   {
  //     skip: !competitionId || sport === 'all',
  //   }
  // );
  // const eventOptions = [
  //   ...(eventsData?.events?.map((e) => ({
  //     label: e.name,
  //     value: e.id,
  //   })) || []),
  // ];

  const items =
    activeResultTab === 'pending'
      ? data?.markets || []
      : rollbackData?.markets || [];

  const totalItems =
    activeResultTab === 'pending'
      ? data?.pagination?.totalItems || 0
      : rollbackData?.pagination?.totalItems || 0;

  // Debounced search
  const debouncedSearch = useRef(
    debounce((value: string) => {
      setSearch(value);
      setPage(1);
    }, 500)
  ).current;

  // Open Result Modal
  const handleOpenResult = async (row: PendingMarketItem) => {
    try {
      setSelectedEventName(row.eventName);
      setEventDetails(row);

      let runnersData = {} as MarketRunners;

      if (row.marketType?.toLowerCase() === 'fancy') {
        runnersData = {
          id: '',
          externalId: '',
          eventId: '',
          status: '',
          runner: [
            {
              runnerName: row.selection || '',
              handicap: 0,
              selectionId: '',
              sortPriority: '',
              runnerId: '',
            },
          ],
        };
      } else {
        const response = await fetchRunner({
          eventId: row.eventId,
          marketId: row.marketId,
        }).unwrap();

        runnersData = response.marketRunners;
      }

      setRunners(runnersData);
      setOpen(true);
    } catch (error) {
      console.error(error);
    }
  };

  // Declare Result
  const handleResultAction = async (
    action: ResultActionType,
    data: Record<string, ResultValue>,
    runs: string
  ) => {
    if (!eventDetails) return;

    const { marketName, marketType, eventExternalId, marketId } = eventDetails;

    const winner = Object.values(data).find((r) => r.status === 'Winner');
    const fancySelectionId = marketType?.toLowerCase() === 'fancy' ? marketId : '';

    const payload: DeclareResultPayload = {
      market: marketName || '',
      marketType: marketType || '',
      eventId: eventExternalId || '',
      marketId: marketId || '',
      selectionId: winner?.selectionId || fancySelectionId || '',
      result: runs || '',
    };
    // console.log(data,runs,payload);

    try {
      const res =
        action === 'declare'
          ? await declareResult(payload).unwrap()
          : await rollbackResult(payload).unwrap();

      toast.success(
        res?.message ||
        (action === 'declare'
          ? 'Result declared successfully!'
          : 'Result rolled back successfully!')
      );

      setOpen(false);
    } catch (err: any) {
      toast.error(err?.data?.message || err?.message || 'Action failed');
    }
  };

  const handleSportChange = (value: string) => {
    setSport(value);

    // if (value === 'all') {
    //   setCompetitionId(undefined);
    //   setEventId(undefined);
    // } else {
    //   setCompetitionId(undefined);
    //   setEventId(undefined);
    // }
  };

  // const handleCompetitionChange = (value: number) => {
  //   setCompetitionId(value);
  //   setEventId(undefined);
  // };
  const isTableLoading =
    activeResultTab === 'pending'
      ? isLoading || (isFetching && !isPollingEnabled)
      : rollbackLoading || (rollbackFetching && !isPollingEnabled);


  return {
    // table data
    items,
    totalItems,
    isTableLoading,
    betResultLoading: isActionLoading,
    // runnersLoading,
    // loadingEventId

    // pagination & filters
    page,
    limit,
    setPage,
    setLimit,
    dateRange,
    setDateRange,
    debouncedSearch,
    isPollingEnabled,
    setIsPollingEnabled,

    // modal
    open,
    setOpen,
    selectedEventName,
    runners,
    marketType: (eventDetails?.marketType || '').toLowerCase(),

    // actions
    handleOpenResult,
    handleResultAction,

    // FILTERS
    sport,
    // competitionId,
    // eventId,
    sportOptions,
    // competitionOptions,
    // eventOptions,
    handleSportChange,
    // handleCompetitionChange,
    // setEventId,
    // competitionLoading,
    // eventsLoading,
    activeResultTab,
    setActiveResultTab
  };
};
